import { Component, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CustomValidators, toIso8601String } from '@bcl/common-ui';
import { SelectOption, today } from '@bcl/design-system';
import { of, Subject, takeUntil } from 'rxjs';
import { ScannerService } from 'src/app/services/scanner.service';

export type EditMonitorDialogData = {
  currentMonitor: {
    moduleName: string;
    typeName: string;
    serialNumber?: string;
    baitNumber?: string;
    statusId?: string;
    outside?: boolean;
    temporary?: boolean;
    startDate?: Date;
    monitorName?: string;
    typeId?: string;
  };
  modules: SelectOption[];
  // types may include a `token` property for BAIT detection
  types: (SelectOption & { token?: string })[];
  statuses: SelectOption[];
};

export type EditMonitorResult = {
  moduleId: string;
  typeId: string;
  statusId: string;
  serialNumber: string;
  baitNumber?: string | null;
  startDate: Date;
  monitorName?: string | null;
  outside: boolean;
  temporary: boolean;
};
@Component({
  selector: 'app-monitor-edit-dialog',
  templateUrl: './monitor-edit-dialog.component.html',
  styleUrls: ['./monitor-edit-dialog.component.scss'],
})
export class MonitorEditDialogComponent implements OnDestroy {
  public form: FormGroup;
  public showBaitNumber = false;
  public scannerActive = false;
  private scannerTargetControlName: 'serialNumber' | 'baitNumber' =
    'serialNumber';
  private readonly destroyed$ = new Subject<void>();
  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: EditMonitorDialogData,
    private readonly dialogRef: MatDialogRef<
      MonitorEditDialogComponent,
      EditMonitorResult
    >,
    private readonly scannerService: ScannerService
  ) {
    this.form = fb.group({
      statusId: [this.data.currentMonitor.statusId, Validators.required],
      serialNumber: [this.data.currentMonitor.serialNumber],
      baitNumber: [this.data.currentMonitor.baitNumber || null],
      startDate: [
        toIso8601String(this.data.currentMonitor.startDate),
        Validators.required,
      ],
      monitorName: [
        this.data.currentMonitor.monitorName,
        [Validators.maxLength(5)],
      ],
      outside: [this.data.currentMonitor.outside],
      temporary: [this.data.currentMonitor.temporary],
    });

    // Determine if bait number should be shown based on selected type token
    const selectedType = this.data.types?.find(
      (t) => (t.value as any) === this.data.currentMonitor.typeId
    ) as (SelectOption & { token?: string }) | undefined;
    this.showBaitNumber = selectedType?.token === 'BAIT';
  }
  public get canSubmit() {
    return this.form.valid;
  }

  public submit() {
    const { startDate, ...rest } = this.form.getRawValue();
    this.dialogRef.close({
      ...rest,
      baitNumber: this.showBaitNumber ? rest.baitNumber : null,
      startDate: new Date(startDate),
    });
  }

  public openScanner(
    target: 'serialNumber' | 'baitNumber' = 'serialNumber'
  ): void {
    this.scannerTargetControlName = target;
    this.scannerActive = true;
    const instanceId = 'monitor-edit-dialog';
    this.scannerService
      .getScanResultObservable(instanceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: string) => {
        this.form.get(this.scannerTargetControlName)?.setValue(result);
        this.scannerActive = false;
      });
  }

  public closeScanner(): void {
    this.scannerActive = false;
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
